import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  configApiRef,
  githubAuthApiRef,
  SessionState,
  useApi,
} from '@backstage/core-plugin-api';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

const HOST = 'github.com';
const DEFAULT_TOPIC = 'app-starter';
const DEFAULT_STARTER = 'ds24-labs/starter-minimal';
// Superset of scopes used across the new-repo step, so a single GitHub grant
// covers both discovery (repo read here) and repo creation (workflow + repo in
// the RepoNameFromApp field). Asking for the superset once avoids a second popup.
const SCOPES = ['repo', 'workflow'];

interface Starter {
  value: string; // owner/repo (GitHub full_name)
  label: string;
  description?: string;
}

// Auto-discovers GitHub template repositories to offer as a starter base.
// A repo qualifies if it is a template repository (is_template) AND carries the
// marker topic (internalApps.starterTopic, default "app-starter"), in any of
// the configured internalApps.githubOrgs. Adding a starter is therefore just a
// repo change (mark as template + add the topic) — no Backstage change needed.
//
// The default starter (internalApps.defaultStarter) is always offered, even
// before discovery, so the form is usable without connecting GitHub. Discovery
// uses the user's GitHub OAuth token (same auth the new-repo flow already uses)
// and reacts to the shared GitHub session — once connected anywhere in this
// step, the list loads automatically.
export const StarterPicker = (props: FieldExtensionComponentProps<string>) => {
  const { onChange, formData } = props;
  const configApi = useApi(configApiRef);
  const githubAuth = useApi(githubAuthApiRef);

  const orgs = useMemo(
    () =>
      configApi.getOptionalStringArray('internalApps.githubOrgs') ?? [
        'ds24-labs',
        'hulkag',
      ],
    [configApi],
  );
  const topic = useMemo(
    () =>
      configApi.getOptionalString('internalApps.starterTopic') ?? DEFAULT_TOPIC,
    [configApi],
  );
  const defaultStarter = useMemo(
    () =>
      configApi.getOptionalString('internalApps.defaultStarter') ??
      DEFAULT_STARTER,
    [configApi],
  );

  const [starters, setStarters] = useState<Starter[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Default to the configured default starter until the user picks otherwise.
  useEffect(() => {
    if (!formData) onChange(defaultStarter);
  }, [formData, onChange, defaultStarter]);

  // interactive=true → opens the GitHub consent popup (the Connect button);
  // interactive=false → silent (no popup), used when reacting to the session.
  const discover = useCallback(
    async (interactive: boolean) => {
      setLoading(true);
      setError(undefined);
      try {
        const token = await githubAuth.getAccessToken(SCOPES, {
          optional: !interactive,
          instantPopup: interactive,
        });
        if (!token) return; // not connected yet (silent attempt)
        const found: Starter[] = [];
        for (const org of orgs) {
          // Topic filter is server-side; is_template has no search qualifier,
          // so filter it client-side.
          const q = encodeURIComponent(`org:${org} topic:${topic}`);
          const res = await fetch(
            `https://api.${HOST}/search/repositories?q=${q}&per_page=50`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/vnd.github+json',
              },
            },
          );
          if (!res.ok) continue;
          const body = await res.json();
          for (const r of body.items ?? []) {
            // Skip non-templates and the default (already offered below).
            if (r.is_template && r.full_name !== defaultStarter) {
              found.push({
                value: r.full_name,
                label: r.full_name,
                description: r.description ?? undefined,
              });
            }
          }
        }
        setStarters(found);
        setLoaded(true);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [githubAuth, orgs, topic, defaultStarter],
  );

  // React to the shared GitHub session — when the user connects GitHub (here or
  // via the repository field), load templates automatically, no extra click.
  useEffect(() => {
    const sub = githubAuth.sessionState$().subscribe(state => {
      if (state === SessionState.SignedIn) discover(false);
    });
    return () => sub.unsubscribe();
  }, [githubAuth, discover]);

  const value = formData ?? defaultStarter;

  let helper = `Connect GitHub to list more template repositories, or keep the default starter.`;
  if (loading) {
    helper = 'Loading template repositories…';
  } else if (error) {
    helper = `Couldn't load templates (${error}). The default starter is still available.`;
  } else if (loaded && starters.length) {
    helper = `Found ${starters.length} more template repo(s) tagged "${topic}". Pick one, or keep the default starter.`;
  } else if (loaded) {
    helper = `No other template repos tagged "${topic}" found — using the default starter. (Mark a repo as a template and add the "${topic}" topic to offer it here.)`;
  }

  let buttonLabel = 'Connect GitHub';
  if (loading) {
    buttonLabel = 'Loading…';
  } else if (loaded) {
    buttonLabel = 'Refresh templates';
  }

  return (
    <FormControl fullWidth margin="normal">
      <InputLabel id="starter-picker-label">Starter template</InputLabel>
      <Select
        labelId="starter-picker-label"
        label="Starter template"
        value={value}
        onChange={e => onChange(e.target.value as string)}
        MenuProps={{
          MenuListProps: {
            sx: { display: 'flex', flexDirection: 'column' },
          },
        }}
      >
        <MenuItem value={defaultStarter}>{defaultStarter} — default</MenuItem>
        {starters.map(s => (
          <MenuItem key={s.value} value={s.value}>
            {s.label}
            {s.description ? ` — ${s.description}` : ''}
          </MenuItem>
        ))}
      </Select>
      <Button
        variant="outlined"
        size="small"
        onClick={() => discover(true)}
        disabled={loading}
        style={{ alignSelf: 'flex-start', marginTop: 8 }}
      >
        {buttonLabel}
      </Button>
      <FormHelperText error={!!error}>{helper}</FormHelperText>
    </FormControl>
  );
};
